import React, { createRef, FunctionComponent } from "react";
import styles from "./BannerSplitImage.module.scss";
import Anchor from "components/Anchor/Anchor";
import BannerSplitImageContent from "./BannerSplitImageContent.model";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useWindowDimensions from "hooks/useWindowDimensions";
import formatDynamicTextBreak from "blocks/helpers/formatDynamicTextBreak";

const BannerSplitImage: FunctionComponent<BannerSplitImageContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextBreak(formatDynamicTextColor(props.content.headline)));
    const processedTagline = getProcessedRichtext(formatDynamicTextBreak(props.content.tagline));
    const wrapperStyles = getWrapperStyles(props.settings);

    const { width } = useWindowDimensions();
    const media = props.content.media;
    const imageCrop = getImageCrop(media?.crops, undefined, width > 768 ? "Square (1:1)" : "Widescreen (16:9)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, media?.width, media?.height);

    const wrapperElementRef = createRef<HTMLDivElement>();
    const clickScrollHandler = () => {
        if (wrapperElementRef.current) {
            const scrollOverThreshold = wrapperElementRef.current.scrollTop + wrapperElementRef.current.offsetHeight + (80 - 60); // --header-height --header-height-small
            window.document.body.scrollTop = scrollOverThreshold;
        }
    };

    return (
        <div className={combineClasses([styles.wrapper, ...wrapperStyles])}>
            <div
                className={styles.container}
                ref={wrapperElementRef}
            >
                {/* Content */}
                <div className={styles.content}>
                    <div className={styles.inner}>
                        {/* Tagline */}
                        <span
                            className={styles.tagline}
                            dangerouslySetInnerHTML={processedTagline}
                        ></span>

                        {/* Headline */}
                        <DynamicHeading
                            className={styles.headline}
                            headlineSize={props.content.headlineSize}
                            headlineWeight={props.content.headlineWeight}
                            innerHtml={processedHeadline}
                        ></DynamicHeading>

                        {/* Buttons */}
                        {props.content.buttons && props.content.buttons.length > 0 ? (
                            <div className={styles.buttons}>
                                {props.content.buttons.map((button) => {
                                    return (
                                        <Anchor
                                            key={button.contentUdi}
                                            to={button?.link}
                                            target={button?.target}
                                            className={styles.button}
                                        >
                                            <span className={styles.button_label}>{button?.label}</span>
                                        </Anchor>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>

                    {/* Scroll Button */}
                    <div
                        className={styles.scrollIcon}
                        onClick={() => clickScrollHandler()}
                    >
                        <IconViewer
                            name="arrowDown"
                            heightRatio={31.552 / 10.848}
                        />
                    </div>
                </div>

                {/* Media */}
                <div className={styles.media}>
                    {media !== null ? (
                        <ImageViewer
                            className={styles.image}
                            src={media?.url || media}
                            crop={imageCrop}
                            focalPoint={media?.focalPoint}
                            {...imageAspectRatio}
                            alt={""}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default BannerSplitImage;
